import React from 'react';
import { Modal } from 'react-bootstrap';

import {
    getActiveBeneficiaryMappingTasks,
    importBeneficiaryMappingPostProcess,
    cancelImportBeneficiaryMapping,
} from '../../apiClient';
import { SPREADSHEET_EXTENSIONS } from '../../constants/file-upload-extensions.constant';
import { MAX_FILES_MAPPING_UPLOAD } from '../../constants/file-upload-config';
import { AsyncTaskStatus } from '../../utils/asyncTasks';

interface MultipleFilesUploadProps {
    onUpload: (files: File[]) => Promise<void>;
    uploadMultipleMappingFiles: (file: File) => Promise<void>;
    onClose: () => void;
    onError?: (error: unknown) => void;
    onValidationClose?: (autoCompleteEnabled?: boolean) => void;
    validationClosed?: boolean;
}

interface TaskInfo {
    filename: string;
    status: string;
    progress?: number;
    autoComplete?: boolean;
}

interface BatchInfo {
    batchId: string;
    tasks: TaskInfo[];
    createdAt: string;
}

interface FilenameDisplayProps {
    tasks: TaskInfo[];
    batches?: BatchInfo[];
}

const FilenameDisplay: React.FC<FilenameDisplayProps> = ({ tasks }) => {
    if (tasks.length === 0) {
        return (
            <div style={{ padding: '20px', textAlign: 'center', border: '1px dashed #ddd', borderRadius: '4px' }}>
                <div style={{ color: '#666', marginBottom: '10px', fontSize: '16px' }}>
                    <i className="fa fa-info-circle" style={{ marginRight: '5px' }} />
                    No files processed yet.
                </div>
                <p style={{ color: '#888', fontSize: '14px', margin: '0' }}>
                    Upload a file to see it here. Data is automatically refreshed every 5 seconds.
                </p>
            </div>
        );
    }
    return (
        <div style={{ marginTop: '20px', marginBottom: '20px' }}>
            <div
                style={{
                    marginBottom: '10px',
                }}
            >
                <h4 style={{ margin: 0, color: '#0071bc' }}>Files Processed</h4>
                <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#666' }}>
                    Data is automatically refreshed every 5 seconds
                </p>
            </div>
            <div
                style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    border: '1px solid #eee',
                    borderRadius: '4px',
                    padding: '10px',
                    backgroundColor: '#f9f9f9',
                }}
            >
                {tasks.map((task, index) => (
                    <div
                        key={index}
                        style={{
                            padding: '8px 10px',
                            marginBottom: index < tasks.length - 1 ? '8px' : 0,
                            borderBottom: index < tasks.length - 1 ? '1px solid #eee' : 'none',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <div
                            style={{
                                marginRight: '10px',
                                color: '#0071bc',
                                fontSize: '16px',
                            }}
                        >
                            <i aria-hidden="true" className="fa fa-file-text-o" />
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 'bold' }}>{task.filename}</div>
                            {task.progress !== undefined && task.progress < 1 ? (
                                <div style={{ marginTop: '5px' }}>
                                    <div
                                        style={{
                                            height: '6px',
                                            backgroundColor: '#e9ecef',
                                            borderRadius: '3px',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: `${task.progress * 100}%`,
                                                height: '100%',
                                                backgroundColor: '#0071bc',
                                                borderRadius: '3px',
                                            }}
                                        />
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                        {Math.round(task.progress * 100)}% complete
                                    </div>
                                </div>
                            ) : (
                                <div style={{ fontSize: '12px', color: '#666' }}>{task.status}</div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export const MultipleFilesUpload: React.FC<MultipleFilesUploadProps> = ({
    onUpload,
    onClose,
    onError,
    onValidationClose,
    validationClosed,
}) => {
    const [files, setFiles] = React.useState<File[]>([]);
    const [isUploading, setIsUploading] = React.useState(false);
    const [isProcessing, setIsProcessing] = React.useState(false);
    const [availableTasks, setAvailableTasks] = React.useState<TaskInfo[]>([]);
    const [availableBatches, setAvailableBatches] = React.useState<BatchInfo[]>([]);
    const [showFinishConfirmDialog, setShowFinishConfirmDialog] = React.useState(false);
    const [showCancelConfirmDialog, setShowCancelConfirmDialog] = React.useState(false);
    const [showInfoModal, setShowInfoModal] = React.useState(false);
    const [infoModalMessage, setInfoModalMessage] = React.useState('');
    const [hadActiveAutoCompleteTasks, setHadActiveAutoCompleteTasks] = React.useState(false);

    const areAllTasksFinished = React.useMemo(() => {
        if (availableTasks.length === 0) {
            return false;
        }

        // Check if every task has a finished status
        return availableTasks.every((task) => {
            const status = task.status;
            return (
                status === AsyncTaskStatus.finished ||
                status === AsyncTaskStatus.failed ||
                status === AsyncTaskStatus.finishedWithWarnings
            );
        });
    }, [availableTasks]);

    const hasReachedMaxFiles = React.useMemo(() => {
        return availableTasks.length >= MAX_FILES_MAPPING_UPLOAD;
    }, [availableTasks]);

    const hasTasksInProgress = React.useMemo(() => {
        return availableTasks.some((task) => {
            const status = task.status;
            return (
                status !== AsyncTaskStatus.finished &&
                status !== AsyncTaskStatus.failed &&
                status !== AsyncTaskStatus.finishedWithWarnings
            );
        });
    }, [availableTasks]);

    const hasActiveAutoCompleteTask = React.useMemo(() => {
        return availableTasks.some((task) => {
            const status = task.status;
            const isActive =
                status !== AsyncTaskStatus.finished &&
                status !== AsyncTaskStatus.failed &&
                status !== AsyncTaskStatus.finishedWithWarnings;
            return isActive && task.autoComplete === true;
        });
    }, [availableTasks]);

    const fetchMappingTasks = React.useCallback(async () => {
        try {
            const response = await getActiveBeneficiaryMappingTasks();
            setAvailableTasks(response.tasks || []);

            // Handle the new batches property if it exists
            if (response.batches) {
                setAvailableBatches(response.batches);
            }
        } catch (error) {
            if (onError) {
                onError(error);
            }
        }
    }, [onError]);

    React.useEffect(() => {
        // Initial fetch
        fetchMappingTasks();

        // Set up interval to fetch data every 5 seconds
        const intervalId = setInterval(() => {
            fetchMappingTasks();
        }, 5000);

        // Clean up interval on component unmount
        return () => clearInterval(intervalId);
    }, [fetchMappingTasks]);

    // Call fetchMappingTasks when validationClosed changes to true
    React.useEffect(() => {
        if (validationClosed) {
            fetchMappingTasks();
            // Reset validationClosed to false after it's been used
            if (onValidationClose) {
                onValidationClose();
            }
        }
    }, [validationClosed, fetchMappingTasks, onValidationClose]);

    // Track when we have active auto-complete tasks
    React.useEffect(() => {
        if (hasActiveAutoCompleteTask) {
            setHadActiveAutoCompleteTasks(true);
        }
    }, [hasActiveAutoCompleteTask]);

    // Close modal when auto-complete tasks finish
    React.useEffect(() => {
        if (hadActiveAutoCompleteTasks && (areAllTasksFinished || availableTasks.length === 0)) {
            setHadActiveAutoCompleteTasks(false);
            onClose();
        }
    }, [hadActiveAutoCompleteTasks, areAllTasksFinished, availableTasks, onClose]);

    const handleButtonClick = () => {
        if (hasActiveAutoCompleteTask) {
            setInfoModalMessage(
                'Auto-finish is enabled. The mapping will finish automatically when current tasks complete.'
            );
            setShowInfoModal(true);
        } else if (hasReachedMaxFiles) {
            setInfoModalMessage(`You can only upload up to ${MAX_FILES_MAPPING_UPLOAD} files at once.`);
            setShowInfoModal(true);
        } else if (hasTasksInProgress) {
            setInfoModalMessage('Please wait for all current tasks to complete before uploading more files.');
            setShowInfoModal(true);
        }
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const newFiles = Array.from(event.target.files || []);

        if (newFiles.length > 1) {
            setInfoModalMessage('You can only upload one file at a time.');
            setShowInfoModal(true);
            event.target.value = '';
            return;
        }

        setIsUploading(true);
        try {
            await onUpload(newFiles);
            setFiles([...files, ...newFiles]);
            fetchMappingTasks();
        } catch (error) {
            onError(error);
        } finally {
            setIsUploading(false);
            event.target.value = '';
        }
    };

    const closeInfoModal = () => {
        setShowInfoModal(false);
    };

    const showFinishConfirmationDialog = () => {
        if (availableTasks.length === 0) {
            onError('No files available for mapping');
            return;
        }
        setShowFinishConfirmDialog(true);
    };

    const hideFinishConfirmationDialog = () => {
        setShowFinishConfirmDialog(false);
    };

    const confirmFinishMapping = async () => {
        setShowFinishConfirmDialog(false);
        setIsProcessing(true);
        try {
            await importBeneficiaryMappingPostProcess();
            await fetchMappingTasks(); // Fetch mapping tasks after successful import
            onClose(); // Close the component after successful mapping
        } catch (error) {
            onError(error);
        } finally {
            setIsProcessing(false);
        }
    };

    const showCancelConfirmationDialog = () => {
        setShowCancelConfirmDialog(true);
    };

    const hideCancelConfirmationDialog = () => {
        setShowCancelConfirmDialog(false);
    };

    const confirmCancelMapping = async () => {
        setShowCancelConfirmDialog(false);
        try {
            await cancelImportBeneficiaryMapping();
            onClose();
        } catch (error) {
            onError(error);
            onClose();
        }
    };

    return (
        <div
            className="upload-section"
            style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '4px',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                marginBottom: '20px',
                position: 'relative',
                minHeight: '300px',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div
                className="upload-header"
                style={{
                    marginBottom: '20px',
                    paddingBottom: '10px',
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <h3 style={{ margin: 0 }}>Upload Mapping Files</h3>
                {hasReachedMaxFiles && (
                    <div style={{ color: '#dc3545', fontSize: '14px' }}>
                        Maximum number of files reached ({MAX_FILES_MAPPING_UPLOAD})
                    </div>
                )}
                {hasActiveAutoCompleteTask && (
                    <div style={{ color: '#dc3545', fontSize: '14px' }}>
                        Auto-finish is enabled - mapping will finish automatically
                    </div>
                )}
            </div>
            <div className="upload-body" style={{ flex: 1 }}>
                <FilenameDisplay batches={availableBatches} tasks={availableTasks} />
            </div>

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 'auto',
                    paddingTop: '20px',
                }}
            >
                <div style={{ display: 'flex', gap: '10px' }}>
                    <div style={{ position: 'relative', overflow: 'hidden' }}>
                        <button
                            className="wfp-btn"
                            disabled={isUploading || hasActiveAutoCompleteTask}
                            onClick={
                                hasActiveAutoCompleteTask || hasReachedMaxFiles || hasTasksInProgress
                                    ? handleButtonClick
                                    : undefined
                            }
                            style={{
                                opacity: isUploading || hasActiveAutoCompleteTask ? 0.6 : 1,
                                cursor: isUploading || hasActiveAutoCompleteTask ? 'not-allowed' : 'pointer',
                            }}
                            type="button"
                        >
                            {isUploading ? 'Uploading...' : 'Upload More Files'}
                        </button>
                        {!hasActiveAutoCompleteTask && !hasReachedMaxFiles && !hasTasksInProgress && (
                            <input
                                accept={SPREADSHEET_EXTENSIONS}
                                disabled={isUploading || hasActiveAutoCompleteTask}
                                multiple
                                name="data"
                                onChange={handleFileChange}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    opacity: 0,
                                    cursor: isUploading || hasActiveAutoCompleteTask ? 'not-allowed' : 'pointer',
                                }}
                                type="file"
                            />
                        )}
                    </div>

                    {availableTasks.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <button
                                className="wfp-btn wfp-btn--primary"
                                disabled={isProcessing || !areAllTasksFinished || hasActiveAutoCompleteTask}
                                onClick={showFinishConfirmationDialog}
                                style={{
                                    backgroundColor: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    padding: '8px 16px',
                                    cursor:
                                        isProcessing || !areAllTasksFinished || hasActiveAutoCompleteTask
                                            ? 'not-allowed'
                                            : 'pointer',
                                    borderRadius: '4px',
                                    fontWeight: 'bold',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                                type="button"
                            >
                                {isProcessing ? (
                                    <>
                                        <div
                                            className="wfp-loading wfp-loading--small"
                                            style={{ marginRight: '8px' }}
                                        />
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-check" style={{ marginRight: '5px' }} />
                                        Finish Mapping
                                    </>
                                )}
                            </button>
                            {hasActiveAutoCompleteTask && (
                                <div
                                    style={{
                                        marginLeft: '10px',
                                        color: '#dc3545',
                                        fontSize: '14px',
                                        fontStyle: 'italic',
                                    }}
                                >
                                    Auto-finish is enabled - mapping will finish automatically
                                </div>
                            )}
                            {!areAllTasksFinished && availableTasks.length > 0 && !hasActiveAutoCompleteTask && (
                                <div
                                    style={{
                                        marginLeft: '10px',
                                        color: '#dc3545',
                                        fontSize: '14px',
                                        fontStyle: 'italic',
                                    }}
                                >
                                    You need to wait for all tasks to be finished to finish mapping
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <button
                    className="wfp-btn"
                    disabled={hasActiveAutoCompleteTask}
                    onClick={showCancelConfirmationDialog}
                    style={{
                        backgroundColor: '#f0f0f0',
                        color: '#333',
                        border: '1px solid #ccc',
                        padding: '8px 16px',
                        cursor: hasActiveAutoCompleteTask ? 'not-allowed' : 'pointer',
                        borderRadius: '4px',
                        opacity: hasActiveAutoCompleteTask ? 0.6 : 1,
                    }}
                    type="button"
                >
                    Cancel Mapping
                </button>
            </div>

            {showFinishConfirmDialog && (
                <Modal backdrop="static" centered keyboard={false} onHide={hideFinishConfirmationDialog} show>
                    <Modal.Header>
                        <Modal.Title>Finish Mapping</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p
                            style={{
                                display: 'block',
                                textOverflow: 'ellipsis',
                                wordWrap: 'break-word',
                                overflow: 'hidden',
                            }}
                        >
                            Are you sure you want to finish the mapping process?
                        </p>
                    </Modal.Body>
                    <Modal.Footer>
                        <button
                            className="wfp-btn"
                            onClick={hideFinishConfirmationDialog}
                            style={{ marginRight: 10 }}
                            type="button"
                        >
                            No
                        </button>
                        <button className="wfp-btn--primary" onClick={confirmFinishMapping} type="submit">
                            Yes
                        </button>
                    </Modal.Footer>
                </Modal>
            )}

            {showCancelConfirmDialog && (
                <Modal backdrop="static" centered keyboard={false} onHide={hideCancelConfirmationDialog} show>
                    <Modal.Header>
                        <Modal.Title>Cancel Mapping</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p
                            style={{
                                display: 'block',
                                textOverflow: 'ellipsis',
                                wordWrap: 'break-word',
                                overflow: 'hidden',
                            }}
                        >
                            Are you sure you want to cancel the mapping process?
                        </p>
                    </Modal.Body>
                    <Modal.Footer>
                        <button
                            className="wfp-btn"
                            onClick={hideCancelConfirmationDialog}
                            style={{ marginRight: 10 }}
                            type="button"
                        >
                            No
                        </button>
                        <button className="wfp-btn--primary" onClick={confirmCancelMapping} type="submit">
                            Yes
                        </button>
                    </Modal.Footer>
                </Modal>
            )}

            {showInfoModal && (
                <Modal backdrop="static" centered keyboard={false} onHide={closeInfoModal} show>
                    <Modal.Header>
                        <Modal.Title>Information</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p
                            style={{
                                display: 'block',
                                textOverflow: 'ellipsis',
                                wordWrap: 'break-word',
                                overflow: 'hidden',
                            }}
                        >
                            {infoModalMessage}
                        </p>
                    </Modal.Body>
                    <Modal.Footer>
                        <button className="wfp-btn--primary" onClick={closeInfoModal} type="button">
                            OK
                        </button>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
};
